import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Search } from 'lucide-react';
import DynamicPageSections from '../components/site/DynamicPageSections';
import { useSiteStructureSections } from '../hooks/useSiteStructureSections';

const faqs = [
  {
    category: 'Orders',
    items: [
      {
        q: 'How do I place an order?',
        a: 'Simply browse our products, add items to your cart, and proceed to checkout. You can pay using credit card, PayPal, or cash on delivery.',
      },
      {
        q: 'Can I modify or cancel my order?',
        a: 'You can modify or cancel your order within 2 hours of placing it. After that, the order enters our processing system.',
      },
      {
        q: 'How do I track my order?',
        a: "Once your order ships, you'll receive a tracking number via email. You can also track it in your account under \"My Orders\".",
      },
    ],
  },
  {
    category: 'Shipping',
    items: [
      {
        q: 'How long does shipping take?',
        a: 'Standard shipping takes 5-7 business days. Express shipping delivers in 2-3 business days. International orders may take 7-14 days.',
      },
      {
        q: 'Do you offer free shipping?',
        a: 'Yes! We offer free standard shipping on all orders over ₹4,999. Express shipping is free for orders over ₹12,499.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes, we ship to over 100 countries worldwide. Shipping rates and delivery times vary by location.',
      },
    ],
  },
  {
    category: 'Payment',
    items: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit cards (Visa, MasterCard, American Express), PayPal, Apple Pay, Google Pay, and Cash on Delivery.',
      },
      {
        q: 'Is my payment information secure?',
        a: 'Absolutely. We use industry-standard SSL encryption and never store your full credit card details.',
      },
      {
        q: 'Can I pay in installments?',
        a: 'Yes, we offer buy-now-pay-later options through Klarna and Afterpay for eligible orders.',
      },
    ],
  },
  {
    category: 'Account',
    items: [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" at the top of the page and fill in your details. You can also check out as a guest.',
      },
      {
        q: 'I forgot my password. What should I do?',
        a: "Click \"Forgot Password\" on the login page and enter your email. We'll send you a reset link.",
      },
      {
        q: 'How do I update my account information?',
        a: 'Log in and go to "My Account" to update your personal details, addresses, and preferences.',
      },
    ],
  },
];

const FAQHeroStatic: React.FC = () => (
  <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-16 md:py-20">
    <div className="container mx-auto px-4">
      <div className="max-w-3xl mx-auto text-center">
        <HelpCircle className="w-16 h-16 mx-auto mb-4" />
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
        <p className="text-xl text-blue-100">Find answers to common questions about our products and services</p>
      </div>
    </div>
  </section>
);

const FAQ: React.FC = () => {
  const { sections, ready } = useSiteStructureSections('/faq');
  const showCmsHeader = ready && sections.length > 0;

  const [openIndex, setOpenIndex] = useState<string | null>('Orders-0');
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFaq = (key: string) => {
    setOpenIndex(openIndex === key ? null : key);
  };

  const filteredFaqs = faqs
    .map((category) => ({
      ...category,
      items: category.items.filter(
        (item) =>
          item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.a.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((category) => category.items.length > 0);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 pt-16">
      <main className="flex-grow">
        {showCmsHeader ? <DynamicPageSections sections={sections} /> : <FAQHeroStatic />}

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="relative max-w-xl mx-auto mb-10">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                />
              </div>

              {searchTerm === '' ? (
                faqs.map((category) => (
                  <div key={category.category} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{category.category}</h2>
                    <div className="space-y-3">
                      {category.items.map((item, idx) => {
                        const key = `${category.category}-${idx}`;
                        return (
                          <div key={key} className="bg-white rounded-xl shadow-sm overflow-hidden">
                            <button
                              type="button"
                              onClick={() => toggleFaq(key)}
                              className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                            >
                              <span className="font-medium text-gray-900">{item.q}</span>
                              {openIndex === key ? (
                                <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              )}
                            </button>
                            {openIndex === key && (
                              <div className="px-5 pb-5">
                                <p className="text-gray-600">{item.a}</p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : filteredFaqs.length > 0 ? (
                <div className="space-y-3">
                  {filteredFaqs.map((category) =>
                    category.items.map((item, idx) => {
                      const key = `${category.category}-${idx}-search`;
                      return (
                        <div key={key} className="bg-white rounded-xl shadow-sm overflow-hidden">
                          <button
                            type="button"
                            onClick={() => toggleFaq(key)}
                            className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                          >
                            <div className="text-left">
                              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider">
                                {category.category}
                              </span>
                              <p className="font-medium text-gray-900 mt-1">{item.q}</p>
                            </div>
                            {openIndex === key ? (
                              <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            )}
                          </button>
                          {openIndex === key && (
                            <div className="px-5 pb-5">
                              <p className="text-gray-600">{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No results found. Try a different search term.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Still have questions?</h2>
            <p className="text-indigo-100 mb-8">Our support team is here to help you</p>
            <a
              href="/contact"
              className="inline-block bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};

export default FAQ;
